import { useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { useCreateFolder } from "../../hooks/useCreateFolder";
import { ApiError } from "../../lib/api/client";
import { validateFolderName } from "../../lib/validation";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";

interface NewFolderModalProps {
  open: boolean;
  parentId: string | null;
  onClose: () => void;
}

export function NewFolderModal({ open, parentId, onClose }: NewFolderModalProps) {
  const titleId = useId();
  const inputId = useId();
  const errorId = useId();
  const [value, setValue] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const createFolder = useCreateFolder(parentId);

  // Reset local state whenever the modal opens.
  useEffect(() => {
    if (open) {
      setValue("");
      setServerError(null);
    }
  }, [open]);

  const validation = validateFolderName(value);
  const canSubmit = validation.valid && !createFolder.isPending;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validation.valid) {
      return;
    }
    setServerError(null);
    createFolder.mutate(
      { name: validation.value },
      {
        onSuccess: () => {
          onClose();
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 400) {
            setServerError(err.message);
            return;
          }
          if (err instanceof ApiError && err.status === 401) {
            return; // global handler redirected
          }
          setServerError(
            err instanceof ApiError ? err.message : "Something went wrong. Please try again.",
          );
        },
      },
    );
  };

  return (
    <Modal open={open} onClose={onClose} labelledBy={titleId} maxWidthClass="max-w-md">
      <form onSubmit={handleSubmit} className="flex flex-col">
        <div className="px-5 pb-2 pt-5">
          <h2 id={titleId} className="text-title font-semibold text-text">
            New folder
          </h2>
        </div>
        <div className="px-5 py-3">
          <label htmlFor={inputId} className="sr-only">
            Folder name
          </label>
          <input
            id={inputId}
            type="text"
            autoFocus
            value={value}
            placeholder="Untitled folder"
            onChange={(e) => {
              setValue(e.target.value);
              if (serverError) {
                setServerError(null);
              }
            }}
            aria-invalid={serverError ? true : undefined}
            aria-describedby={serverError ? errorId : undefined}
            className="w-full rounded-md border border-surface-border bg-surface px-3 py-2 text-body text-text placeholder:text-text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          />
          {serverError ? (
            <p id={errorId} className="mt-2 text-body text-danger-700">
              {serverError}
            </p>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-surface-border px-5 py-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {createFolder.isPending ? "Creating…" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
